/**
 * Graph Utilities Module
 * Contains utility functions for graph filtering and calculations
 */

// Import the DOT converter
import { act } from 'react';
import { DotToCytoscapeConverter } from './dot-converter.js';

/**
 * Get set of node IDs to keep based on the filter percentage
 * @param {Object} processData - Process data containing activity percentiles
 * @param {number} nodeFilterPercentage - Current node filter percentage
 * @param {string} processStartId - Process start identifier
 * @param {string} processEndId - Process end identifier
 * @returns {Set} - Set of node IDs to keep
 */
export function getFilteredNodes(processData, nodeFilterPercentage, processStartId, processEndId) {
    const nodesToKeep = new Set();
    
    // Always keep Process Start and Process End
    if (processStartId) nodesToKeep.add(processStartId);
    if (processEndId) nodesToKeep.add(processEndId);
    
    // Check if activityPercentiles is available
    if (processData && processData.activities) {
        // Use activity information to determine which nodes to keep
        for (const [activity, activityDetails] of Object.entries(processData.activities)) {
            // Keep the node if its percentile is less than or equal to the current filter percentage
            if (activityDetails.percentile <= nodeFilterPercentage) {
                nodesToKeep.add(activity);
            }
            // #FILTER_OPTION
            // if (activityDetails.frequency > nodeFilterPercentage) {
            //     nodesToKeep.add(activity);
            // }
        }
    }
    
    return nodesToKeep;
}

/**
 * Get set of edge IDs to keep based on the filter percentage and visible nodes
 * @param {Object} processData - Process data containing edges
 * @param {number} edgeFilterPercentage - Current edge filter percentage
 * @param {Set} nodesToKeep - Set of node IDs to keep
 * @returns {Set} - Set of edge IDs to keep
 */
export function getFilteredEdges(processData, edgeFilterPercentage, nodesToKeep) {
    const edgesToKeep = new Set();
    
    // Check if edges have percentile information
    const edgesHavePercentiles = processData.edges.length > 0 && 
        processData.edges[0].hasOwnProperty('percentile');
    
    if (edgesHavePercentiles) {
        // Filter edges using percentile information
        processData.edges.forEach(edge => {
            // Only keep edges that are:
            // 1. Between visible nodes, AND
            // 2. Have a percentile <= current filter percentage
            if (nodesToKeep.has(edge.source) && 
                nodesToKeep.has(edge.target) && 
                edge.percentile <= edgeFilterPercentage) { 
                edgesToKeep.add(`${edge.source}-${edge.target}`);
            }

            // #FILTER_OPTION

        });
    } 
    
    return edgesToKeep;
}

/**
 * Calculate the minimum node filter percentage needed to show all nodes in the most common path
 * @param {Object} processData - Process data containing most common path and activity percentiles
 * @param {string} processStartId - Process start identifier
 * @param {string} processEndId - Process end identifier
 * @returns {number} - Minimum node filter percentage
 */
export function calculateMinNodeFilterPercentage(processData, processStartId, processEndId) {
    if (!processData || !processData.mostCommonPath) {
        return 5; // Default minimum if data not available
    }

    const pathNodes = new Set(processData.mostCommonPath.filter(
        node => node !== processStartId && node !== processEndId
    ));

    // #FILTER_OPTION
    // minimum percentage is the highest percentage within the common path

    const percentilesInCommonPath = new Set();
    if (processData && processData.activities) {
        // Use percentile information to determine which nodes to keep

        Object.entries(processData.activities).forEach(([activity, activityDetails]) => {
            // if activity is in pathNodes, add percentile to percentilesInCommonPath
            if (pathNodes.has(activity)) {
                percentilesInCommonPath.add(activityDetails.percentile);
            }
        });
    }

    const min_percentile = Math.max(...percentilesInCommonPath);
    return min_percentile;
}

/**
 * Calculate the minimum edge filter percentage needed to ensure connectivity
 * @param {Object} processData - Process data containing edges
 * @param {number} nodeFilterPercentage - Current node filter percentage
 * @param {string} processStartId - Process start identifier
 * @param {string} processEndId - Process end identifier
 * @returns {number} - Minimum edge filter percentage
 */
export function calculateMinEdgeFilterPercentage(processData, nodeFilterPercentage, processStartId, processEndId) {
    if (!processData || !processData.edges || processData.edges.length === 0) {
        return 5; // Default minimum if data not available
    }

    // #FILTER_OPTION
    // Get nodes that are currently visible based on node filter
    const visibleNodeIds = new Set();
    const nodesToKeep = getFilteredNodes(processData, nodeFilterPercentage, processStartId, processEndId);
    
    // Add all nodes that would be visible with current node filter
    nodesToKeep.forEach(nodeId => {
        visibleNodeIds.add(nodeId);
    });

    // Remove start and end nodes
    if (processStartId) visibleNodeIds.delete(processStartId);
    if (processEndId) visibleNodeIds.delete(processEndId);

    // collect all edges that are the highest incoming for some visible node
    const necessaryEdges = new Set();

    visibleNodeIds.forEach(nodeId => {
        // get all incoming edges for this node
        const incomingEdgesForNode = processData.edges.filter(edge => edge.target === nodeId);

        // find the entry in incomingEdgesForNode that has the highest count
        if (incomingEdgesForNode.length > 0) {
            const highestIncomingEdge = incomingEdgesForNode.reduce((edgeA, edgeB) => {
                if (edgeA.count > edgeB.count) { return edgeA; } 
                else {return edgeB;}
            });
            necessaryEdges.add(highestIncomingEdge);
        }

        // get all outgoing edges for this node
        const outgoingEdgesForNode = processData.edges.filter(edge => edge.source === nodeId);

        // find the entry in outgoingEdgesForNode that has the highest count
        if (outgoingEdgesForNode.length > 0) {
            const highestOutgoingEdge = outgoingEdgesForNode.reduce((edgeA, edgeB) => {
                if (edgeA.count > edgeB.count) { return edgeA; } 
                else {return edgeB;}
            });
            necessaryEdges.add(highestOutgoingEdge);
        }
    });

    console.debug("NECESSARY EDGES");
    console.debug(necessaryEdges);

    // get the lowest percentile among the necessary edges
    const maxPercentile = Math.max(...Array.from(necessaryEdges).map(edge => edge.percentile));

    console.debug("MAX PERCENTILE EDGES");
    console.debug(maxPercentile);
    
    // Return at least 5% but not more than 100%
    return Math.min(Math.max(maxPercentile, 5), 100);
}

/**
 * Create a DOT string representation of the filtered graph
 * @param {Object} processData - Process data containing nodes, edges, and activity frequencies
 * @param {number} nodeFilterPercentage - Current node filter percentage
 * @param {number} edgeFilterPercentage - Current edge filter percentage
 * @param {string} processStartId - Process start identifier
 * @param {string} processEndId - Process end identifier
 * @returns {string} - DOT format string
 */
export function create_dot_string(processData, nodeFilterPercentage, edgeFilterPercentage, processStartId, processEndId) {
    if (!processData || !processData.edges || !processData.activities) {
        return "digraph G {\n  // Empty graph - no data available\n}";
    }

    // Get filtered nodes and edges based on current filter percentages
    const nodesToKeep = getFilteredNodes(processData, nodeFilterPercentage, processStartId, processEndId);
    const edgesToKeep = getFilteredEdges(processData, edgeFilterPercentage, nodesToKeep);

    let dotScript = "digraph ProcessGraph {\n";
    
    // Add graph attributes
    dotScript += "  // Graph attributes\n";
    dotScript += "  graph [rankdir=TB, fontname=Arial, splines=true];\n";
    dotScript += "  node [fontname=Arial, shape=box, style=filled];\n";
    dotScript += "  edge [fontname=Arial];\n\n";
    
    // Add nodes
    dotScript += "  // Nodes\n";
    nodesToKeep.forEach(nodeId => {
        console.debug(processData.activities);
        const count = processData.activities[nodeId].count || 0;
        const label = `${nodeId}\\n${count}`;
        const safeId = safeGraphvizId(nodeId);
        
        // Determine node color based on node type
        let color = "#3498db"; // default color
        if (nodeId === processStartId) {
            color = "#2ecc71"; // start node color
        } else if (nodeId === processEndId) {
            color = "#e74c3c"; // end node color
        } else if (count <= (Math.max(...Object.values(processData.activities).map(activity => activity.count)) * 0.05)) {
            color = "#95a5a6"; // rare node color
        }
        
        dotScript += `  ${safeId} [label="${label}", color="${color}", fillcolor="${color}", fontcolor="white"];\n`;
    });
    
    // Add edges
    dotScript += "\n  // Edges\n";
    processData.edges.forEach(edge => {
        const edgeId = `${edge.source}-${edge.target}`;
        
        // Only include edges that are in the filtered set and between visible nodes
        if (edgesToKeep.has(edgeId) && nodesToKeep.has(edge.source) && nodesToKeep.has(edge.target)) {
            const source = safeGraphvizId(edge.source);
            const target = safeGraphvizId(edge.target);
            const count = edge.count || '';
            
            // Edge attributes
            let edgeAttributes = '';
            if (count) {
                edgeAttributes += `label="${count}"`;
            }
            
            // Style process boundary edges differently
            if (edge.source === processStartId || edge.target === processEndId) {
                edgeAttributes += edgeAttributes ? ', ' : '';
                edgeAttributes += 'style=dashed, color="#95a5a6", penwidth=0.5';
            }
            
            if (edgeAttributes) {
                dotScript += `  ${source} -> ${target} [${edgeAttributes}];\n`;
            } else {
                dotScript += `  ${source} -> ${target};\n`;
            }
        }
    });
    
    dotScript += "}";
    return dotScript;
}

/**
 * Make ID safe for Graphviz by replacing problematic characters
 * @param {string} id - Original ID
 * @returns {string} - Safe ID for Graphviz
 */
export function safeGraphvizId(id) {
    // Escape special characters and spaces
    return `"${id.replace(/"/g, '\\"')}"`;
}

/**
 * Convert DOT string to Cytoscape elements using the DOT converter
 * @param {string} dotString - DOT format string
 * @returns {Promise<Object>} - Promise that resolves to Cytoscape elements
 */
export async function convertDotString(dotString) {
    try {
        console.log("Converting DOT string to Cytoscape elements...");
        
        // Create a new converter instance
        const converter = new DotToCytoscapeConverter();
        
        // Convert the DOT string to Cytoscape elements
        const elements = await converter.convertToCytoscape(dotString);
        
        console.log("DOT conversion completed successfully");
        return elements;
    } catch (error) {
        console.error("Error converting DOT string:", error);
        throw error;
    }
}
